import { useEffect, useSyncExternalStore } from "react";
import { zoneForCoords, type MrPandaZone } from "@/lib/mrpanda-zone";
import { detectMrPandaZone } from "@/lib/mrpanda-zone.functions";

const CACHE_KEY = "mp-delivery-zone-v2";
const TTL_OK = 12 * 60 * 60 * 1000; // 12 ώρες όταν εξυπηρετείται


type Cached = { zone: MrPandaZone | null; at: number };

let currentZone: MrPandaZone | null = null;
const listeners = new Set<() => void>();

function publishZone(zone: MrPandaZone | null) {
  currentZone = zone;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Ζητά την τοποθεσία ΜΟΝΟ ύστερα από ρητή ενέργεια του χρήστη
 * (π.χ. κλικ σε «Έλεγχος διεύθυνσης»). Δεν καλείται ποτέ αυτόματα.
 */
export function requestMrPandaZoneLocation(): Promise<MrPandaZone | null> {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const z = zoneForCoords(pos.coords.latitude, pos.coords.longitude);
        publishZone(z);
        try {
          if (z?.serviceable) {
            localStorage.setItem(CACHE_KEY, JSON.stringify({ zone: z, at: Date.now() } as Cached));
          }
        } catch {
          /* ignore */
        }
        resolve(z);
      },
      () => resolve(null),
      { maximumAge: 30 * 60_000, timeout: 8000 },
    );
  });
}

/**
 * Εντοπίζει αν ο χρήστης βρίσκεται στην περιοχή Παράδοσης MrPanda.
 * 1) θετικό cache → 2) GPS μόνο αν υπάρχει ήδη άδεια → 3) geo δικτύου.
 * Δεν εμφανίζεται ποτέ prompt τοποθεσίας κατά τη φόρτωση.
 */

export function useMrPandaZone(): MrPandaZone | null {
  const zone = useSyncExternalStore(subscribe, () => currentZone, () => null);

  useEffect(() => {
    let cancelled = false;

    // Χειροκίνητη δοκιμή/επιβολή: ?mpzone=1 (εντός) ή ?mpzone=0 (εκτός)
    const force = new URLSearchParams(window.location.search).get("mpzone");
    if (force === "1") {
      publishZone({ serviceable: true, distanceKm: 4, freeOver: 25, cost: 3 });
      return;
    }
    if (force === "0") {
      publishZone(null);
      return;
    }

    const save = (z: MrPandaZone | null) => {
      if (cancelled) return;
      publishZone(z);
      try {
        // Δεν αποθηκεύουμε αποτυχία εντοπισμού: ένα προσωρινό σφάλμα δικτύου
        // δεν πρέπει να κρύβει την ενημέρωση για ώρες.
        if (z?.serviceable) {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ zone: z, at: Date.now() } as Cached));
        } else {
          localStorage.removeItem(CACHE_KEY);
        }
      } catch {
        /* ignore */
      }
    };

    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        const c = JSON.parse(raw) as Cached;
        if (c.zone?.serviceable && Date.now() - c.at < TTL_OK) {
          publishZone(c.zone);
          return;
        }
        localStorage.removeItem(CACHE_KEY);
      }
    } catch {
      /* ignore */
    }

    const gps = (onFail: () => void) =>
      navigator.geolocation.getCurrentPosition(
        (pos) => save(zoneForCoords(pos.coords.latitude, pos.coords.longitude)),
        onFail,
        { maximumAge: 30 * 60_000, timeout: 8000 },
      );

    const run = async () => {
      try {
        const perm = await navigator.permissions
          ?.query({ name: "geolocation" as PermissionName })
          .catch(() => null);
        // ΜΟΝΟ αν ο χρήστης έχει ήδη δώσει άδεια. Ποτέ δεν εμφανίζουμε
        // prompt τοποθεσίας κατά τη φόρτωση της σελίδας — αυτό γίνεται
        // αποκλειστικά μετά από ενέργεια του χρήστη
        // (βλ. requestMrPandaZoneLocation).
        if (perm?.state === "granted" && navigator.geolocation) {
          gps(() => void networkFallback());
          return;
        }
      } catch {
        /* ignore */
      }
      await networkFallback();
    };

    const networkFallback = async () => {
      let z: MrPandaZone | null = null;
      try {
        z = (await detectMrPandaZone()) ?? null;
      } catch {
        z = null;
      }
      save(z);
    };

    // Η ένδειξη βρίσκεται ψηλά στη σελίδα, οπότε ο εντοπισμός ξεκινά άμεσα
    // αντί να περιμένει αβέβαιο idle χρόνο.
    void run();


    return () => {
      cancelled = true;
    };
  }, []);

  return zone;
}
